import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { NotificationTextMessage } from '@/core/enums/notification';
import type { SupportTicketCreated, SupportTicketOptions } from '@/core/models/workspace.models';
import { notifyError } from '@/core/notify/notify';
import { workspaceService } from '@/core/services/workspace';
import { Button } from '@/kit/Button';
import { DialogClose, DialogContent, type DialogComponentProps } from '@/kit/dialog';
import { InputField, TextareaField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { Select } from '@/kit/Select';
import './AddSupportTicket.scss';

const DEFAULT_MAX_FILE_BYTES = 2 * 1024 * 1024;
const DEFAULT_EXTENSIONS = ['.doc', '.docx', '.pdf', '.jpg', '.png', '.jpeg', '.txt', '.xls', '.xlsx', '.csv'];
const MAX_FILES = 10;

/** Angular `FormValidationService.validateForm`: true when valid, otherwise the mandatory-fields toast. */
function validateForm(isValid: boolean): boolean {
  if (isValid) {
    return true;
  }
  notifyError(NotificationTextMessage.validationMessage);
  return false;
}

interface TicketForm {
  name: string;
  email: string;
  phone: string;
  company: string | null;
  helpTopic: string | null;
  module: string | null;
  subject: string;
  message: string;
}

const emptyForm: TicketForm = {
  name: '',
  email: '',
  phone: '',
  company: null,
  helpTopic: null,
  module: null,
  subject: '',
  message: '',
};

type TextField = 'phone' | 'subject' | 'message';

/** The reactive-form validators: phone required + /^[0-9]{7,15}$/, subject required + max 255, message required. */
function isFieldInvalid(field: TextField, value: string): boolean {
  // The API trims the subject and message, so a value made only of spaces is as empty as no value.
  if (value.length === 0 || (field !== 'phone' && value.trim().length === 0)) {
    return true;
  }
  if (field === 'phone') {
    return !/^[0-9]{7,15}$/.test(value);
  }
  if (field === 'subject') {
    return value.length > 255;
  }
  return false;
}

/** The per-field message under a field that failed (`isFieldInvalid`): required, or the phone's 7 to 15 digits. */
function fieldMessage(field: TextField, value: string): string {
  if (field === 'phone' && value.length > 0) {
    return 'Phone number must be 7 to 15 digits.';
  }
  return 'This field is required.';
}

/** Raise a support ticket with optional attachments (multipart upload). Closes with `SupportTicketCreated` on success. */
export function AddSupportTicket({ dialogRef }: DialogComponentProps<unknown, SupportTicketCreated>) {
  const [form, setForm] = useState<TicketForm>(emptyForm);
  const [options, setOptions] = useState<SupportTicketOptions | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const saving = useRef(false);
  /** ng-touched: fields the user left, or every field after a failed submit. */
  const [touched, setTouched] = useState<Set<TextField>>(new Set());
  /** The phone typed so far, read when the options arrive (`ticketForm.value.phone || options.phone || ''`). */
  const phoneRef = useRef('');

  const acceptedExtensions = (options?.allowedExtensions ?? DEFAULT_EXTENSIONS).join(',');

  useEffect(() => {
    workspaceService.getSupportTicketOptions().then(
      (loaded) => {
        setOptions(loaded);
        setForm((current) => ({
          ...current,
          name: loaded.name,
          email: loaded.email,
          phone: phoneRef.current || loaded.phone || '',
          company: loaded.companies.length === 1 ? loaded.companies[0].id : null,
        }));
      },
      (error) => notifyError(error),
    );
  }, []);

  const set = <K extends keyof TicketForm>(key: K, value: TicketForm[K]) => {
    if (key === 'phone') {
      phoneRef.current = value as string;
    }
    setForm((current) => ({ ...current, [key]: value }));
  };

  const touch = (field: TextField) => setTouched((current) => new Set(current).add(field));
  const invalid = (field: TextField) => touched.has(field) && isFieldInvalid(field, form[field]);
  const message = (field: TextField) =>
    invalid(field) ? (
      <span className="text-red font-size-12">{fieldMessage(field, form[field])}</span>
    ) : null;

  const onFilesSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const maxBytes = options?.maxFileBytes ?? DEFAULT_MAX_FILE_BYTES;
    const extensions = options?.allowedExtensions ?? DEFAULT_EXTENSIONS;
    const next = [...files];

    Array.from(input.files ?? []).forEach((file) => {
      const extension = file.name.includes('.') ? file.name.substring(file.name.lastIndexOf('.')).toLowerCase() : '';
      if (!extensions.includes(extension)) {
        notifyError(`${file.name} is not a supported file type.`);
      } else if (file.size === 0) {
        notifyError(`${file.name} is empty.`);
      } else if (file.size > maxBytes) {
        notifyError(`${file.name} is larger than 2 MB.`);
      } else if (next.length >= MAX_FILES) {
        notifyError(`Attach at most ${MAX_FILES} files.`);
      } else if (!next.some((f) => f.name === file.name && f.size === file.size)) {
        next.push(file);
      }
    });

    setFiles(next);
    input.value = '';
  };

  const removeFile = (index: number) => setFiles((current) => current.filter((_, i) => i !== index));

  const onSubmit = () => {
    if (saving.current) {
      return;
    }
    const isValid =
      !isFieldInvalid('phone', form.phone) &&
      !isFieldInvalid('subject', form.subject) &&
      !isFieldInvalid('message', form.message);
    if (!validateForm(isValid)) {
      setTouched(new Set<TextField>(['phone', 'subject', 'message']));
      return;
    }
    saving.current = true;
    setIsSaving(true);
    workspaceService
      .saveSupportTicket({
        phone: form.phone,
        company: form.company,
        helpTopic: form.helpTopic,
        module: form.module,
        subject: form.subject,
        message: form.message,
        files,
      })
      .then(
        (created) => dialogRef.close(created),
        (error) => notifyError(error),
      )
      .finally(() => {
        saving.current = false;
        setIsSaving(false);
      });
  };

  return (
    <div className="mat-dialog-wrapper ticket-popup">
      <DialogContent>
        <form onSubmit={(event) => event.preventDefault()}>
          <div className="main-contain">
            <div className="sidenav-header">
              <div className="d-flex align-items-center justify-space-between">
                <h2 className="m-0">
                  <span>New Support Ticket</span>
                </h2>
                <DialogClose>
                  <Icon name="close" className="close" />
                </DialogClose>
              </div>
            </div>
          </div>
          <div className="sidenav-body">
            <div className="d-flex gap-20 mb-1">
              <div className="form-field w-230">
                <p className="fw-bold">
                  Name <span className="text-red">*</span>
                </p>
                <InputField
                  type="text"
                  charMode="text"
                  value={form.name}
                  maxLength={255}
                  readOnly
                  onChange={() => undefined}
                />
              </div>
              <div className="form-field w-230">
                <p className="fw-bold">
                  Email <span className="text-red">*</span>
                </p>
                <InputField value={form.email} maxLength={320} readOnly onChange={() => undefined} />
              </div>
              <div className="form-field w-230">
                <p className="fw-bold">
                  Phone <span className="text-red">*</span>
                </p>
                <InputField
                  type="tel"
                  charMode="number"
                  minLength={0}
                  maxLength={15}
                  value={form.phone}
                  invalid={invalid('phone')}
                  onKeyPress={(event) => {
                    if (!(event.charCode >= 48 && event.charCode <= 57)) event.preventDefault();
                  }}
                  onChange={(event) => set('phone', event.target.value)}
                  onBlur={() => touch('phone')}
                />
                {message('phone')}
              </div>
            </div>
            <div className="d-flex gap-20 mb-1">
              <div className="form-field w-230">
                <p className="fw-bold">Company</p>
                <Select
                  items={options?.companies}
                  bindLabel="name"
                  bindValue="id"
                  value={form.company}
                  onChange={(value) => set('company', value as string | null)}
                  panelClass="myPanelClass"
                />
              </div>
              <div className="form-field w-230">
                <p className="fw-bold">Help Topic</p>
                <Select
                  items={options?.helpTopics}
                  bindLabel="name"
                  bindValue="id"
                  value={form.helpTopic}
                  onChange={(value) => set('helpTopic', value as string | null)}
                  panelClass="myPanelClass"
                />
              </div>
              <div className="form-field w-230">
                <p className="fw-bold">Module</p>
                <Select
                  items={options?.modules}
                  bindLabel="name"
                  bindValue="id"
                  value={form.module}
                  onChange={(value) => set('module', value as string | null)}
                  panelClass="myPanelClass"
                />
              </div>
            </div>
            <div className="hr-top pt-1 mt-5 mb-1">
              <div className="form-field w-434">
                <p className="fw-bold">
                  Subject <span className="text-red">*</span>
                </p>
                <InputField
                  type="text"
                  maxLength={255}
                  value={form.subject}
                  invalid={invalid('subject')}
                  onChange={(event) => set('subject', event.target.value)}
                  onBlur={() => touch('subject')}
                />
                {message('subject')}
              </div>
            </div>
            <div>
              <div className="form-field textarea mb-1">
                <p className="fw-bold">
                  Message <span className="text-red">*</span>
                </p>
                <TextareaField
                  fieldClassName="wp-100"
                  maxLength={4000}
                  value={form.message}
                  invalid={invalid('message')}
                  onChange={(event) => set('message', event.target.value)}
                  onBlur={() => touch('message')}
                />
                {message('message')}
              </div>
              <div className="form-field attached">
                <p className="fw-bold">Attachments</p>
                <div className="form-field attached mb-10">
                  <div className="attachFile">
                    <Icon name="cloud_upload" outlined />
                    <input
                      multiple
                      type="file"
                      autoComplete="off"
                      maxLength={100}
                      id="file"
                      accept={acceptedExtensions}
                      onChange={onFilesSelected}
                      className="cursor-pointer"
                    />
                    <label htmlFor="file" className="cursor-pointer">
                      <strong>Choose a file</strong> or drag it here to attach a file with ticket.
                    </label>
                  </div>
                </div>
                <span className="font-size-12">
                  (Max file size - 2 MB) | Supported formats : .doc, .docx, .pdf, .jpg, .png, .jpeg, .txt, .xls, .xlsx,
                  .csv
                </span>
                {files.length > 0 && (
                  <div className="note-table mt-10">
                    <div className="table-border">
                      {files.map((file, index) => (
                        <div
                          key={`${file.name}-${file.size}-${index}`}
                          className="clickable d-flex align-items-center justify-space-between"
                        >
                          <div>{file.name}</div>
                          <div className="close mt-5">
                            <a
                              href="#"
                              aria-label={`Remove ${file.name}`}
                              onClick={(event) => {
                                event.preventDefault();
                                removeFile(index);
                              }}
                            >
                              <Icon name="close" />
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="d-flex gap-15 hr-top pt-1 mt-1">
            <Button className="action-button primary-button" busy={isSaving} onClick={onSubmit}>
              Submit
            </Button>
            <DialogClose as="button" type="button" className="action-button secondary-button">
              Cancel
            </DialogClose>
          </div>
        </form>
      </DialogContent>
    </div>
  );
}
